import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { Element } from "@stencil/core";
import {
  getFormattedDate,
  isDateDisabled,
  daysInMonth,
  getFirstDayOfMonth,
  isCurrentMonth,
  monthNames,
  weekDays,
  moveContainerPosition,
  getFormattedDateWithSlashes,
  isStringDateValid,
  getDateParts,
  abbreviatedMonthNames,
} from "../../utils/datePickerUtils";

@Component({
  tag: "signet-date-picker",
  styleUrl: "signet-date-picker.scss",
  shadow: false,
})
export class SignetDatePicker implements ComponentInterface {
  @State() selectedDate: Date | null = null;
  @State() dummyDateValue: Date | null = null;
  @State() defaultDateValue: Date | null = null;
  @State() formattedDate = "";
  @State() currentMonth: number;
  @State() currentYear: number;
  @State() showCalendar = false;
  @State() placeholder = "MM/DD/YYYY";
  @State() calendarContainerElement?: HTMLElement;
  @State() dateInputElement?: HTMLInputElement;
  @State() isInputHovered? = false;
  @State() isInputFocused? = false;
  @State() isMonthSelectorVisible = false;
  @State() isYearSelectorVisible = false;
  @State() yearRangeStart: number;
  @State() view: "years" | "months" | "days" = "days";
  @State() isDateValid: boolean;
  @State() invalidDate: string;
  @State() hasTyped = false;

  @Element() el!: HTMLElement;


  /**
   * The value of the date in string format
   */
  @Prop() date: string;
  /**
   * A boolean indicating whether the date picker is disabled
   */
  @Prop() disabled = false;
  /**
   * The locale for formatting the date (e.g., 'en-US', 'en-GB')
   */
  @Prop() locale = "en-US";
  /**
   * The minimum selectable date the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() minDate: string;
  /**
   * The maximun selectable date of the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() maxDate: string;
  /**
   * The dummy date of the date range in string format (e.g., 'MM/DD/YYYY')
   */
  @Prop() dummyDate: string;
  /**
   * The default date value of the calendar (e.g., 'MM/DD/YYYY')
   */
  @Prop() defaultValue: string;
  /**
   * A boolean indicating whether the date picker is in a warning state with yellow border color.
   */
  @Prop() warning: boolean;
  /**
   * A boolean indicating whether the date picker is in an error state with red border color.
   */
  @Prop() error: boolean;
  /**
   * The label for the date input field
   */
  @Prop() label = "Select Date";
  /**
   * A boolean indicating whether to show clear button
   */
  @Prop() clearable = false;
  /**
   * If true, the date picker is required and a colored asterisk will be shown in the label.
   */
  @Prop() required = false;
  /**
   * The color of the required asterisk in the label. Defaults to 'red'.
   */
  @Prop() requiredAsteriskColor = 'red';

  /**
   * The size of the date picker input. Can be 'sm' | 'small', 'md' | 'medium', or 'lg' | 'large'.
   * Affects the input's padding and height.
   * - sm/small: 32px
   * - md/medium: 42px (default)
   * - lg/large: 52px
   */
  @Prop() size: 'sm' | 'small' | 'md' | 'medium' | 'lg' | 'large' = 'medium';

  private getNormalizedSize() {
    switch (this.size) {
      case 'sm':
      case 'small':
        return 'small';
      case 'lg':
      case 'large':
        return 'large';
      case 'md':
      case 'medium':
      default:
        return 'medium';
    }
  }

  /**
   * An event emitter that fires when an invalid date is entered.
   * It emits an error object containing details about the invalid input.
   */
  @Event() dateError: EventEmitter<{
    message: string;
    invalidDate: string;
    field: "date";
    timestamp: Date;
  }>;

  /**
   * An event emitter that fires when the selected date changes
   */
  @Event() dateChange: EventEmitter<string>;
  /**
   * This event is emitted when the input element of the date picker loses focus. It can be used to perform actions or handle events when the date picker is no longer in focus.
   */
  @Event({ bubbles: true, composed: true })
  handleBlur: EventEmitter<FocusEvent>;
  /**
   * This event is emitted when the input element of the date picker gains focus. It can be used to perform actions or handle events when the date picker is focused, such as showing a calendar or changing the input format.
   */
  @Event({ bubbles: true, composed: true })
  handleFocus: EventEmitter<FocusEvent>;

  @Watch("showCalendar")
  @Watch("selectedDate")
  @Watch("defaultDateValue")
  updateCalendarUiDefaultMonthAndYearValues() {
    let dateToDisplayInCalendarUI = new Date();

    if (this.defaultDateValue) {
      dateToDisplayInCalendarUI = this.defaultDateValue;
    }

    if (this.selectedDate) {
      dateToDisplayInCalendarUI = this.selectedDate;
    }

    this.currentMonth = dateToDisplayInCalendarUI.getMonth();
    this.currentYear = dateToDisplayInCalendarUI.getFullYear();
    this.yearRangeStart =
      Math.floor(dateToDisplayInCalendarUI.getFullYear() / 10) * 10;
  }

  @Watch("date")
  updateSelectedDateValue(newValue: string) {
    this.selectedDate = newValue ? new Date(newValue) : null;
  }

  @Watch("selectedDate")
  setFormatToSelectedDateValue() {
    this.formattedDate = getFormattedDate(this.selectedDate, this.locale);

    if (this.dateInputElement && !this.hasTyped) {
      this.dateInputElement.value = this.formattedDate;
    }
  }

  @Watch("dummyDate")
  updateDummyDateValue(newValue: string) {
    this.dummyDateValue = newValue ? new Date(newValue) : null;
  }

  @Watch("defaultValue")
  updateDefaultValue(newValue: string) {
    this.defaultDateValue = newValue ? new Date(newValue) : null;
  }


  private updatedDateValues() {
    this.date = this.selectedDate ? this.selectedDate.toISOString() : "";

    this.emitChangeEvent();

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: this.date
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: this.date
    }));
  }

  componentWillLoad() {
    if (this.locale === "en-GB") {
      this.placeholder = "DD/MM/YYYY";
    } else if (this.locale === "en-US") {
      this.placeholder = "MM/DD/YYYY";
    }


    this.updateSelectedDateValue(this.date);
    this.updateDummyDateValue(this.dummyDate);
    this.updateDefaultValue(this.defaultValue);
  }

  componentDidLoad() {
    this.moveCalendarContainerToDocumentBody();
  }

  private moveCalendarContainerToDocumentBody() {
    if (this.calendarContainerElement) {
      document.body.appendChild(this.calendarContainerElement);
    }
  }

  private showNextMonth() {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
  }

  private showPreviousMonth() {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
  }

  private selectMonth(monthIndex: number) {
    this.currentMonth = monthIndex;
    this.view = "days";
  }

  private showPreviousYearRange() {
    this.yearRangeStart -= 10;
  }

  private showNextYearRange() {
    this.yearRangeStart += 10;
  }

  private selectYear(year: number) {
    this.currentYear = year;
    this.view = "months";
  }

  private showYearSelector() {
    this.view = "years";
  }

  private showPreviousYear() {
    this.currentYear--;
  }

  private showNextYear() {
    this.currentYear++;
  }

  private showMonthSelector() {
    this.view = "months";
  }

  private emitChangeEvent() {
    console.log("Our Date Changed:", this.selectedDate);
    this.dateChange.emit(getFormattedDate(this.selectedDate, this.locale));
  }

  private emitBlurEvent(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    
  
    if (isStringDateValid(rawValue, this.locale)) {
      const { day, month, year } = getDateParts(rawValue, this.locale);
  
      const formatted =
        this.locale === "en-GB"
          ? `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`
          : `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
  
      input.value = formatted;
      this.formattedDate = formatted;
    }
  
    if (this.invalidDate?.length <= 9 && this.invalidDate.length > 0) {
      this.isDateValid = false;
    } else {
      this.isDateValid = true;
    }
    console.log("Input Blurred:", event);
    this.handleBlur.emit(event);
  }

  private emitFocusEvent(event: FocusEvent) {
    console.log("Input Focused:", event);
    this.handleFocus.emit(event);
  }

  // private handleDateSelectedByKeyboard(event: KeyboardEvent) {
  //   event.preventDefault();
  //   const input = event.target as HTMLInputElement;
  //   const rawValue = input.value;

  //   this.handleClearDate(rawValue);
    
  //   // Format only when value is exactly 8 digits (MMDDYYYY)
  //   const cleanedValue = rawValue.replace(/\D/g, "");
  //   if (cleanedValue.length === 8) {
  //     const formatted = getFormattedDateWithSlashes(cleanedValue);
  //     input.value = formatted;
  //   }

  //   if (!isStringDateValid(input.value, this.locale) && input.value.length == 10) {
  //     this.isDateValid = false;
  //     const dateError = {
  //       message: `Invalid date entered: ${input.value}`,
  //       invalidDate: input.value,
  //       field: "date" as "date",
  //       timestamp: new Date(),
  //     };
  //     console.error(dateError);
  //     this.dateError.emit(dateError); 
  //   } else {
  //     this.isDateValid = true;
  //   }
  //   if (isStringDateValid(input.value, this.locale)) {
  //     const { day, month, year } = getDateParts(input.value, this.locale);
  //     this.setDate( parseInt(day, 10), parseInt(month, 10) - 1, parseInt(year, 10));
  //     this.invalidDate = "";
  //   } else {
  //     this.invalidDate = input.value;
  //   }
  // }

  // private handleDateSelectedByKeyboard(event: Event) {
  //   const input = event.target as HTMLInputElement;
  //   const rawValue = input.value;
  
  //   this.handleClearDate(rawValue);
  
  //   const cleanedValue = rawValue.replace(/\D/g, "");
  
  //   // Format only when we have full MMDDYYYY
  //   if (cleanedValue.length === 8) {
  //     const formatted = getFormattedDateWithSlashes(cleanedValue);
  //     input.value = formatted;
  //   }
  
  //   // ✅ Only process and set date if it's valid
  //   if (isStringDateValid(input.value, this.locale)) {
  //     this.isDateValid = true;
  //     const { day, month, year } = getDateParts(input.value, this.locale);
  //     this.setDate(parseInt(day), parseInt(month) - 1, parseInt(year));
  //     this.invalidDate = "";
  //   } else {
  //     // ❌ Don't try to coerce it — just store the invalid input and emit error
  //     this.isDateValid = false;
  //     this.invalidDate = input.value;
  
  //     const error = {
  //       message: `Invalid date entered: ${input.value}`,
  //       invalidDate: input.value,
  //       field: "date" as const,
  //       timestamp: new Date(),
  //     };
  //     console.error(error);
  //     this.dateError.emit(error);
  //   }
  // }

  private handleDateSelectedByKeyboard(event: Event) {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    this.hasTyped = true;
  
    this.handleClearDate(rawValue);
  
    const cleanedValue = rawValue.replace(/\D/g, "");
    if (cleanedValue.length === 8) {
      const formatted = getFormattedDateWithSlashes(cleanedValue);
      input.value = formatted;
    
      if (isStringDateValid(formatted, this.locale)) {
        const { day, month, year } = getDateParts(formatted, this.locale);
        const parsedDay = parseInt(day, 10);
        const parsedMonth = parseInt(month, 10) - 1;
        const parsedYear = parseInt(year, 10);
        const date = new Date(parsedYear, parsedMonth, parsedDay);
    
        if (
          date.getFullYear() === parsedYear &&
          date.getMonth() === parsedMonth &&
          date.getDate() === parsedDay
        ) {
          this.selectedDate = date;
          this.invalidDate = "";
          this.isDateValid = true;
          this.updatedDateValues();
        } else {
          this.markInvalidInput(input.value);
        }
      } else {
        this.markInvalidInput(input.value);
      }
    }
  }

  private markInvalidInput(value: string) {
    this.invalidDate = value;
    this.isDateValid = false;
  
    const dateError = {
      message: `Invalid date entered: ${value}`,
      invalidDate: value,
      field: "date" as const,
      timestamp: new Date(),
    };
    console.error(dateError);
    this.dateError.emit(dateError);
  }

  private handleClearDate(value: string) {
    console.log("handleClearDates", value);
    let isSomeValueCleared = false;
    if ( value.length === 0 &&  (!this.isInputFocused || this.isInputHovered) ) {
      this.selectedDate = null;
      isSomeValueCleared = true;
    }

    if ( value.length === 0 &&  (!this.isInputFocused || this.isInputHovered) ) {
      this.selectedDate = null;
      isSomeValueCleared = true;
    }

    if (isSomeValueCleared) {
      this.updatedDateValues();
    }
  }

  private handleDateSelectedByUI(day: number, month: number, year: number) {
    const date = new Date(year, month, day);

    if (isDateDisabled(date, this.minDate, this.maxDate)) {
      return;
    }

    this.setDate(day, month, year);
  }

  private setDate(day: number, month: number, year: number) {
    const date = new Date(year, month, day);
  
    // Optional: double-check that Date wasn’t silently corrected
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
      console.warn("Date auto-corrected by JS, ignoring...");
      return;
    }
  
    this.selectedDate = date;
    this.updatedDateValues();
  
    if (!this.selectedDate) {
      this.dateInputElement.focus();
    } else {
      this.closeCalendar();
    }
  }

  private handleInputFocus() {
    this.adjustCalendarContainerPosition();

    this.calendarContainerElement.style.visibility = "visible";
    this.showCalendar = true;
  }



  private adjustCalendarContainerPosition() {
    if (!this.dateInputElement || !this.calendarContainerElement) {
      return;
    }

    moveContainerPosition(this.dateInputElement, this.calendarContainerElement);
  }

  private closeCalendar() {
    this.calendarContainerElement.style.visibility = "hidden";
    this.showCalendar = false;
    this.view = "days";
  }

  private showClearButton() {
    return (
      this.clearable &&
      this.selectedDate &&
      this.isInputHovered &&
      !this.disabled
    );
  }

  private handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (!this.calendarContainerElement) {
      return;
    }

    if (
      !this.calendarContainerElement.contains(target) &&
      document.activeElement !== this.dateInputElement
    ) {
      this.closeCalendar();
    }
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick.bind(this));
  }

  private handleDummyDate() {
    this.selectedDate = this.dummyDateValue;

    this.updatedDateValues();
    this.closeCalendar();
  }
  render() {
    const inputClassNames = {
      disabled: this.disabled,
      error: this.error || this.isDateValid == false,
      warning: this.warning,
    };

    const daysInCurrentMonth = daysInMonth(this.currentMonth, this.currentYear);
    const firstDayOfMonth = getFirstDayOfMonth(
      this.currentMonth,
      this.currentYear
    );

    const yearRange = Array.from(
      { length: 10 },
      (_, index) => this.yearRangeStart + index
    );

    return (
      <Host>
        <div class="signet-date-picker">
          <div
            class="input-container"
            onMouseEnter={() => (this.isInputHovered = true)}
            onMouseLeave={() => (this.isInputHovered = false)}
            onKeyDown={() => {this.isInputFocused = true}}
            onKeyUp={() => {this.isInputFocused = false}}
          >
            <label htmlFor="date" class="input-label">
              {this.label}
              {this.required && (
                <span style={{ color: this.requiredAsteriskColor, marginLeft: '2px' }}>*</span>
              )}
            </label>
            <input
              type="text"
              id="date"
              ref={(e) => (this.dateInputElement = e as HTMLInputElement)}
              value={this.formattedDate}
              onInput={(evt) =>
                this.handleDateSelectedByKeyboard(evt)
              }
              onFocus={(evt) => {
                this.handleInputFocus();
                this.emitFocusEvent(evt);
              }}
              onBlur={(evt) => {
                this.emitBlurEvent(evt);
              }}
              placeholder={this.placeholder}
              disabled={this.disabled}
              class={{
                ...inputClassNames,
                'input-size-small': this.getNormalizedSize() === 'small',
                'input-size-medium': this.getNormalizedSize() === 'medium',
                'input-size-large': this.getNormalizedSize() === 'large',
              }}
            />
            {this.showClearButton() && (
              <span
                class="input-clear-button"
                onClick={() => this.handleClearDate(this.formattedDate)}
              >
                &#x2715;
              </span>
            )}
          </div>

          <div
            class="signet-date-picker__calendar-container-wrapper"
            ref={(e) => (this.calendarContainerElement = e as HTMLElement)}
            style={{ visibility: "hidden" }}
          >
            {this.dummyDate ? (
              <div class="shortcuts">
                <div
                  class="shortcut-button"
                  onClick={() => this.handleDummyDate()}
                >
                  Dummy Date
                </div>
              </div>
            ) : (
              ""
            )}

            <div class="calendar-container-content">
              {this.view === "years" ? (
                <div>
                  <div class="year-selector">
                    <signet-icon
                      class="calendar-icon left"
                      icon="chevron_left"
                      onClick={() => this.showPreviousYearRange()}
                    />
                    <span class="year-range">
                      {this.yearRangeStart} - {this.yearRangeStart + 9}
                    </span>
                    <signet-icon
                      class="calendar-icon right"
                      icon="chevron_right"
                      onClick={() => this.showNextYearRange()}
                    />
                  </div>
                  <div class="year-selector-content">
                    {yearRange.map((year) => (
                      <div
                        class="year-item"
                        onClick={() => this.selectYear(year)}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                </div>
              ) : this.view === "months" ? (
                <div>
                  <div class="year-selector">
                    <signet-icon
                      class="calendar-icon left"
                      icon="chevron_left"
                      onClick={() => this.showPreviousYear()}
                    />
                    <span class="year">{this.currentYear}</span>
                    <signet-icon
                      class="calendar-icon right"
                      icon="chevron_right"
                      onClick={() => this.showNextYear()}
                    />
                  </div>
                  <div class="month-selector">
                    {abbreviatedMonthNames.map((month, index) => (
                      <div
                        class="month-item"
                        onClick={() => this.selectMonth(index)}
                      >
                        {month}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div class="calendar-header">
                    <h2 class="month-year">
                      <span
                        class="month"
                        onClick={() => this.showMonthSelector()}
                      >
                        {monthNames[this.currentMonth]}
                      </span>{" "}
                      <span
                        class="year"
                        onClick={() => this.showYearSelector()}
                      >
                        {this.currentYear}
                      </span>
                    </h2>

                    <div>
                      <signet-icon
                        class="calendar-icon left"
                        icon="chevron_left"
                        onClick={() => this.showPreviousMonth()}
                      />
                      <signet-icon
                        class="calendar-icon right"
                        icon="chevron_right"
                        onClick={() => this.showNextMonth()}
                      />
                    </div>
                  </div>
                  <div class="weekdays">
                    {weekDays.map((day) => (
                      <div class="weekday">{day}</div>
                    ))}
                  </div>
                  <div class="days">
                    {Array.from({ length: firstDayOfMonth }, (_, index) => (
                      <div class="day blank" key={index}></div>
                    ))}
                    {Array.from({ length: daysInCurrentMonth }, (_, index) => {
                      const day = index + 1;
                      return (
                        <div
                          class={`day ${
                            isCurrentMonth(
                              day,
                              this.currentMonth,
                              this.currentYear
                            )
                              ? "current-day"
                              : ""
                          } ${
                            this.selectedDate &&
                            day === this.selectedDate.getDate() &&
                            this.currentMonth ===
                              this.selectedDate.getMonth() &&
                            this.currentYear === this.selectedDate.getFullYear()
                              ? "selected-day"
                              : ""
                          } ${
                            isDateDisabled(
                              new Date(
                                this.currentYear,
                                this.currentMonth,
                                day
                              ),
                              this.minDate,
                              this.maxDate
                            )
                              ? "disabled-day"
                              : ""
                          }`}
                          key={day}
                          data-day={day}
                          data-month={this.currentMonth}
                          data-year={this.currentYear}
                          onClick={() =>
                            this.handleDateSelectedByUI(
                              day,
                              this.currentMonth,
                              this.currentYear
                            )
                          }
                        >
                          {day}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
